import { useState, useEffect, useRef } from 'react';
import { sendEmailVerification, reload } from 'firebase/auth';
import { auth } from '../../firebase';
import { Mail, Loader2, Shield, AlertCircle, CheckCircle } from 'lucide-react';
import { isBrazilianUser } from '../../utils/detectBrazil';
import { emailVerificationPtBR } from './mappings/emailVerification.pt-br';

const EmailVerification = ({ user, onVerificationComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Always show "email sent" because Login.js already sends it during signup
  const [emailSent] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Track manual resends only
  const hasResentEmail = useRef(false);

  // Check if user is Brazilian for translation
  const isBrazilian = isBrazilianUser();

  // Translation helper
  const t = (key) => {
    if (isBrazilian && emailVerificationPtBR[key]) {
      return emailVerificationPtBR[key];
    }
    return key;
  };


  // Check verification status every 3 seconds
  useEffect(() => {
    if (!user) return;

    let isMounted = true; // Track if component is still mounted

    const checkVerification = async () => {
      // Don't proceed if component unmounted
      if (!isMounted) return;

      try {
        await reload(user);
        // Check again after async operation
        if (isMounted && user.emailVerified) {
          onVerificationComplete();
        }
      } catch (error) {
        console.log('Error checking verification:', error);
      }
    };

    const interval = setInterval(checkVerification, 3000);

    return () => {
      isMounted = false; // Mark as unmounted
      clearInterval(interval);
    };
  }, [user, onVerificationComplete]);

  const handleSendVerification = async () => {
    if (!user) return;

    // Prevent rapid duplicate resends
    if (hasResentEmail.current) {
      console.log('⏭️ Recently resent email, please wait before trying again');
      setError(t('Please wait a moment before resending.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendEmailVerification(user, {
        url: window.location.origin,
        handleCodeInApp: false
      });

      hasResentEmail.current = true;
      setError('');
      console.log('✅ Verification email resent successfully');

      // Reset the flag after 60 seconds to allow another resend
      setTimeout(() => {
        hasResentEmail.current = false;
      }, 60000);

    } catch (error) {
      console.error('❌ Error resending verification email:', error);

      if (error.code === 'auth/too-many-requests') {
        setError(t('Too many requests. Please wait a few minutes before trying again.'));
      } else {
        setError(t('Failed to send verification email. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckNow = async () => {
    setCheckingStatus(true);
    setError('');

    try {
      await reload(user);
      if (user.emailVerified) {
        // Verification complete - call callback which will reload page
        onVerificationComplete();
        // Don't set any state after this point - component will unmount
      } else {
        setError(t('Email not verified yet. Please check your email and click the verification link.'));
        setCheckingStatus(false);
      }
    } catch (error) {
      setError(t('Error checking verification status. Please try again.'));
      setCheckingStatus(false);
    }
    // Don't put finally block here - state updates after reload cause the error
  };

  // NOTE: Auto-send removed - Login.js handles sending verification email during signup
  // This component now only handles:
  // 1. Displaying verification UI
  // 2. Checking verification status
  // 3. Manual resend button

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050311] p-4">
      <div className="max-w-md w-full space-y-8 bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-6 w-6 text-emerald-400" />
          </div>
          <h2 className="text-3xl font-bold text-white">{t('Verify Your Email')}</h2>
          <p className="mt-2 text-gray-400">
            {t('One-time verification to prevent duplicate accounts')}
          </p>

          {/* Security notice */}
          <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-emerald-300 text-sm font-medium">{t('Account Security')}</p>
                <p className="text-emerald-300/70 text-xs mt-0.5">
                  {t('This prevents multiple accounts with the same email address.')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Email address display */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-sm text-gray-400 mb-1">{t('Verification email sent to:')}</p>
            <p className="text-white font-medium">{user?.email}</p>
          </div>

          {/* Status messages */}
          {emailSent && !error && (
            <div className="flex items-start gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-300 text-sm font-medium">{t('Verification Email Sent!')}</p>
                <p className="text-emerald-300/70 text-xs mt-0.5">
                  {t('Check your email and click the verification link. This page will automatically update.')}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-red-400 text-sm">{error}</div>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-3">
            <button
              onClick={handleCheckNow}
              disabled={checkingStatus}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {checkingStatus ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t('Checking...')}
                </>
              ) : (
                t('I Verified My Email')
              )}
            </button>

            <button
              onClick={handleSendVerification}
              disabled={loading}
              className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center border border-white/10"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {t('Sending...')}
                </>
              ) : (
                t('Resend Verification Email')
              )}
            </button>
          </div>

          {/* Help text */}
          <div className="text-center text-xs text-gray-500 space-y-1">
            <p>{t('• Check your spam/junk folder if you don\'t see the email')}</p>
            <p>{t('• The verification link expires in 1 hour')}</p>
            <p>{t('• This page will automatically detect when you verify')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
